/**
 * PLP Filter Sidebar — collapsible filter groups for desktop.
 */
(function () {
  const { html, useState, useEffect } = window.preact;

  function ChevronIcon({ open }) {
    return html`
      <svg class="plp-filter-group__arrow${open ? ' plp-filter-group__arrow--open' : ''}" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  function CheckIcon() {
    return html`
      <svg class="plp-filter-option__check" width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 4l3 3 5-6" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  function FilterGroup({ title, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);

    return html`
      <div class="plp-filter-group">
        <button class="plp-filter-group__header" onClick=${() => setOpen(!open)}>
          <span>${title}</span>
          <${ChevronIcon} open=${open} />
        </button>
        <div class="plp-filter-group__body${open ? ' plp-filter-group__body--open' : ''}">
          ${children}
        </div>
      </div>
    `;
  }

  var STAR_GLYPHS = {
    5: '★★★★★',
    4: '★★★★☆',
    3: '★★★☆☆',
    2: '★★☆☆☆',
    1: '★☆☆☆☆',
  };

  function isRatingFacet(groupLabel, attribute) {
    return /rating/i.test(groupLabel || '') || /rating/i.test(attribute || '');
  }

  function ratingToStars(label) {
    var n = parseInt(String(label).match(/[1-5]/)?.[0] || '', 10);
    return STAR_GLYPHS[n] || null;
  }

  function FacetOptionLabel({ label, isRating }) {
    if (isRating) {
      var stars = ratingToStars(label);
      if (stars) {
        return html`<span aria-label=${label}>${stars}</span>`;
      }
    }
    return html`<span>${label}</span>`;
  }

  function CheckboxFilter({ options, selected = [], onToggle, isRating = false }) {
    return html`
      <div class="plp-filter-group__options">
        ${Object.entries(options).map(
          ([label, count]) => html`
            <button
              key=${label}
              class="plp-filter-option${selected.includes(label) ? ' plp-filter-option--active' : ''}"
              onClick=${() => onToggle(label)}
            >
              <span class="plp-filter-option__left">
                <span class="plp-filter-option__checkbox">
                  <${CheckIcon} />
                </span>
                <${FacetOptionLabel} label=${label} isRating=${isRating} />
              </span>
              <span class="plp-filter-option__count">${count}</span>
            </button>
          `
        )}
      </div>
    `;
  }

  function PriceRangeFilter({ min, max, currentMin, currentMax, onChangeMin, onChangeMax }) {
    return html`
      <div class="plp-filter-price">
        <input
          class="plp-filter-price__input"
          type="number"
          placeholder="Min"
          min=${min}
          max=${max}
          value=${currentMin || ''}
          onInput=${(e) => onChangeMin(e.target.value ? Number(e.target.value) * 100 : null)}
          aria-label="Minimum price"
        />
        <span class="plp-filter-price__sep">to</span>
        <input
          class="plp-filter-price__input"
          type="number"
          placeholder="Max"
          min=${min}
          max=${max}
          value=${currentMax || ''}
          onInput=${(e) => onChangeMax(e.target.value ? Number(e.target.value) * 100 : null)}
          aria-label="Maximum price"
        />
      </div>
    `;
  }

  function storeBreadcrumbContext(pageType, collectionTitle, collectionUrl, searchQuery) {
    sessionStorage.removeItem('algolia_breadcrumb_path');
    if (pageType === 'collection' && collectionTitle) {
      sessionStorage.setItem('algolia_breadcrumb_collection_url', collectionUrl);
      sessionStorage.setItem('algolia_breadcrumb_collection_title', collectionTitle);
      // Mega-menu ancestor chain — read by the PDP breadcrumb so it shows the
      // same trail the collection PLP showed.
      var bcParents = (window.algoliaCollectionContext && window.algoliaCollectionContext.parents) || [];
      sessionStorage.setItem('algolia_breadcrumb_collection_parents', JSON.stringify(bcParents));
      sessionStorage.removeItem('algolia_breadcrumb_search_query');
    } else if (pageType === 'search' && searchQuery) {
      sessionStorage.setItem('algolia_breadcrumb_search_query', searchQuery);
      sessionStorage.removeItem('algolia_breadcrumb_collection_url');
      sessionStorage.removeItem('algolia_breadcrumb_collection_title');
      sessionStorage.removeItem('algolia_breadcrumb_collection_parents');
    }
    sessionStorage.setItem('algolia_breadcrumb_plp_url', window.location.pathname + window.location.search);
  }

  function PlpBreadcrumbs({ collectionTitle, collectionUrl, pageType, searchQuery }) {
    useEffect(function () {
      storeBreadcrumbContext(pageType, collectionTitle, collectionUrl, searchQuery);
    }, [pageType, collectionTitle, collectionUrl, searchQuery]);

    var pageLabel = pageType === 'collection' ? collectionTitle : (searchQuery ? '"' + searchQuery + '"' : null);
    // Mega-menu ancestor chain (algolia-native-config.liquid) — collection only.
    var parents = (pageType === 'collection' && window.algoliaCollectionContext && window.algoliaCollectionContext.parents) || [];

    return html`
      <nav class="breadcrumbs" role="navigation" aria-label="breadcrumbs">
        <ol class="breadcrumbs__list">
          <li class="breadcrumbs__item"><a class="breadcrumbs__link" href="/">Home</a></li>
          ${parents.map(function (p) {
            return p && p.title && html`
              <li class="breadcrumbs__separator" aria-hidden="true">/</li>
              <li class="breadcrumbs__item">
                ${p.url && p.url !== '#'
                  ? html`<a class="breadcrumbs__link" href="${p.url}">${p.title}</a>`
                  : html`<span class="breadcrumbs__link">${p.title}</span>`}
              </li>
            `;
          })}
          ${pageLabel && html`
            <li class="breadcrumbs__separator" aria-hidden="true">/</li>
            <li class="breadcrumbs__item">
              <span class="breadcrumbs__link" aria-current="page">${pageLabel}</span>
            </li>
          `}
        </ol>
      </nav>
    `;
  }

  // Render one facet group (checkbox list or price range) from a group descriptor.
  function renderFacetGroup(g, filters, onToggleFilter, onPriceChange) {
    const { FilterGroup, CheckboxFilter, PriceRangeFilter } = window.PlpFilterComponents;
    if (g.type === 'price') {
      return html`
        <${FilterGroup} key=${g.attribute} title=${g.label} defaultOpen=${true}>
          <${PriceRangeFilter}
            min=${g.min / 100}
            max=${g.max / 100}
            currentMin=${filters.priceRange?.min != null ? filters.priceRange.min / 100 : null}
            currentMax=${filters.priceRange?.max != null ? filters.priceRange.max / 100 : null}
            onChangeMin=${(val) => onPriceChange('min', val)}
            onChangeMax=${(val) => onPriceChange('max', val)}
          />
        <//>
      `;
    }
    return html`
      <${FilterGroup} key=${g.attribute} title=${g.label} defaultOpen=${true}>
        <${CheckboxFilter}
          options=${g.values}
          selected=${filters[g.attribute] || []}
          onToggle=${(val) => onToggleFilter(g.attribute, val)}
          isRating=${isRatingFacet(g.label, g.attribute)}
        />
      <//>
    `;
  }

  function FilterSidebar({ facets, filters, onToggleFilter, onPriceChange, collectionTitle, collectionUrl, pageType, searchQuery }) {
    var groups = Array.isArray(facets) ? facets : [];

    return html`
      <aside class="plp-filters plp-layout__sidebar">
        <${PlpBreadcrumbs}
          collectionTitle=${collectionTitle}
          collectionUrl=${collectionUrl}
          pageType=${pageType}
          searchQuery=${searchQuery}
        />
        ${groups.map((g) => renderFacetGroup(g, filters, onToggleFilter, onPriceChange))}
      </aside>
    `;
  }

  // Export both individual components (for drawer reuse) and the full sidebar
  window.PlpFilterSidebar = FilterSidebar;
  window.PlpFilterComponents = { FilterGroup, CheckboxFilter, PriceRangeFilter, renderFacetGroup };
})();
